import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import { createContext, useContext, useMemo } from "react";
import { AlignFeature } from "./features/align";
import { CsvExportFeature } from "./features/csv-export";
import { HiddenFeature } from "./features/hidden";
import { IdHeaderFeature } from "./features/id-header";

const GridContext = createContext();

const NO_DATA = []; // stable reference is important here

export function useGrid({
    data = NO_DATA,
    _features = [],
    columns,
    enableSorting,
    ...options
}) {
    const defaults = useMemo(
        () => ({
            data,
            columns: columns
                ? columns
                : data?.[0]
                ? Object.keys(data?.[0]).map((key) => ({
                      accessorKey: key,
                  }))
                : [],
            _features: [
                IdHeaderFeature,
                HiddenFeature,
                AlignFeature,
                CsvExportFeature,
                ..._features,
            ],
            enableSorting,
            getCoreRowModel: getCoreRowModel(),
            getSortedRowModel: enableSorting && getSortedRowModel(),
        }),
        [data, columns, enableSorting, _features]
    );

    return useReactTable({
        ...defaults,
        ...options,
    });
}

export const column = createColumnHelper();

export const render = {
    header: (header) =>
        flexRender(header.column.columnDef.header, header.getContext()),
    cell: (cell) => flexRender(cell.column.columnDef.cell, cell.getContext()),
    footer: (footer) =>
        flexRender(footer.column.columnDef.footer, footer.getContext()),
};

export function useGridContext() {
    return useContext(GridContext);
}

export function Root({ grid, ...props }) {
    return <GridContext.Provider {...props} value={grid} />;
}
Root.displayName = "Grid.Root";
